import { PRICING } from './site';
/**
 * ROUTE_META — Single source of truth for breadcrumb hierarchy and related-page linking.
 * Used by Breadcrumbs and RelatedPages components across all marketing + city pages.
 */

export type RouteMeta = {
  label: string;           // Display name for breadcrumb + related card title
  description: string;     // One-liner for related-pages cards + llms.txt
  category: 'primary' | 'service' | 'city' | 'optional';
  relatedTags: string[];   // Tags for cross-linking scoring
  parents?: Array<{ path: string; label: string }>;  // Breadcrumb trail above this page
};

export const ROUTE_META: Record<string, RouteMeta> = {
  // ──────────────────────────────────────────────────────────
  // Primary Pages
  // ──────────────────────────────────────────────────────────
  '/': {
    label: 'Home',
    description: 'Company overview, transparent pricing, real reviews, primary CTA.',
    category: 'primary',
    relatedTags: ['nlc', 'residential', 'pricing'],
  },
  '/about': {
    label: 'About',
    description: 'Company story, values (integrity, stewardship, community), commitment to W-2 employment.',
    category: 'primary',
    relatedTags: ['nlc', 'values', 'employment'],
  },
  '/services': {
    label: 'Services',
    description: 'All residential cleaning types we offer.',
    category: 'primary',
    relatedTags: ['residential', 'recurring', 'one-time', 'services'],
    parents: [],
  },
  '/pricing': {
    label: 'Pricing',
    description: `Transparent per-square-foot pricing with ${PRICING.minimum} minimum, no hidden fees.`,
    category: 'primary',
    relatedTags: ['pricing', 'residential', 'rates'],
  },
  '/service-areas': {
    label: 'Service Areas',
    description: 'Geographic service area — Greater Tampa Bay residential cleaning.',
    category: 'primary',
    relatedTags: ['locations', 'cities', 'florida'],
  },
  '/reviews': {
    label: 'Reviews',
    description: 'Real Yelp reviews from real Tampa Bay homes.',
    category: 'primary',
    relatedTags: ['reviews', 'testimonials', 'rating'],
  },
  '/contact': {
    label: 'Contact',
    description: 'Contact form, phone, email, hours.',
    category: 'primary',
    relatedTags: ['contact', 'nlc'],
  },
  '/get-a-quote': {
    label: 'Get a Quote',
    description: 'Quote request form with live per-sqft pricing calculator.',
    category: 'primary',
    relatedTags: ['quote', 'pricing', 'booking'],
  },
  '/quote': {
    label: 'Quote Wizard',
    description: 'Multi-step quote wizard — pick service, frequency, and home size to get an exact price.',
    category: 'primary',
    relatedTags: ['quote', 'pricing', 'calculator'],
  },
  '/apply': {
    label: 'Careers',
    description: 'Apply to be a cleaning technician — W-2 employment, fair wages, benefits.',
    category: 'primary',
    relatedTags: ['careers', 'employment', 'hiring'],
  },

  // ──────────────────────────────────────────────────────────
  // Service Pages
  // ──────────────────────────────────────────────────────────
  '/residential': {
    label: 'Residential Cleaning',
    description: 'Residential cleaning service — what is included, how it works, pricing.',
    category: 'service',
    relatedTags: ['residential', 'services', 'recurring'],
    parents: [{ path: '/services', label: 'Services' }],
  },
  '/recurring': {
    label: 'Recurring Cleaning',
    description: 'Recurring cleaning overview — compare weekly, biweekly, and every-four-weeks schedules side by side.',
    category: 'service',
    relatedTags: ['recurring', 'services', 'pricing'],
    parents: [{ path: '/services', label: 'Services' }],
  },
  '/weekly': {
    label: 'Weekly Cleaning',
    description: `Weekly recurring cleaning at ${PRICING.weeklyRate.toFixed(2)}/sqft, our lowest rate.`,
    category: 'service',
    relatedTags: ['recurring', 'pricing', 'weekly'],
    parents: [
      { path: '/services', label: 'Services' },
      { path: '/recurring', label: 'Recurring' },
    ],
  },
  '/biweekly': {
    label: 'Biweekly Cleaning',
    description: `Biweekly recurring cleaning at ${PRICING.biweeklyRate.toFixed(2)}/sqft, our most popular schedule.`,
    category: 'service',
    relatedTags: ['recurring', 'pricing', 'biweekly'],
    parents: [
      { path: '/services', label: 'Services' },
      { path: '/recurring', label: 'Recurring' },
    ],
  },
  '/monthly': {
    label: 'Every-4-Weeks Cleaning',
    description: `Every-four-weeks recurring cleaning at ${PRICING.monthly4Rate.toFixed(2)}/sqft.`,
    category: 'service',
    relatedTags: ['recurring', 'pricing', 'monthly'],
    parents: [
      { path: '/services', label: 'Services' },
      { path: '/recurring', label: 'Recurring' },
    ],
  },
  '/maintenance': {
    label: 'Maintenance Clean',
    description: `One-time maintenance clean at ${PRICING.maintenanceRate.toFixed(2)}/sqft — refresh between deep cleans.`,
    category: 'service',
    relatedTags: ['pricing', 'one-time', 'residential'],
    parents: [{ path: '/services', label: 'Services' }],
  },
  '/deep': {
    label: 'Deep Clean',
    description: `One-time deep clean at ${PRICING.fullDeepRate.toFixed(2)}/sqft — top-to-bottom thorough reset.`,
    category: 'service',
    relatedTags: ['pricing', 'one-time', 'residential'],
    parents: [{ path: '/services', label: 'Services' }],
  },
  '/move-in-out': {
    label: 'Move-In / Move-Out',
    description: `Unfurnished move-in or move-out clean at ${PRICING.moveOutRate.toFixed(2)}/sqft (walls excluded).`,
    category: 'service',
    relatedTags: ['pricing', 'one-time', 'unfurnished'],
    parents: [{ path: '/services', label: 'Services' }],
  },

  // ──────────────────────────────────────────────────────────
  // City Pages
  // ──────────────────────────────────────────────────────────
  '/areas/st-pete': {
    label: 'St. Petersburg',
    description: 'St. Petersburg — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/tampa': {
    label: 'Tampa',
    description: 'Tampa — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/clearwater': {
    label: 'Clearwater',
    description: 'Clearwater — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/largo': {
    label: 'Largo',
    description: 'Largo — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/dunedin': {
    label: 'Dunedin',
    description: 'Dunedin — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/palm-harbor': {
    label: 'Palm Harbor',
    description: 'Palm Harbor — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/safety-harbor': {
    label: 'Safety Harbor',
    description: 'Safety Harbor — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/seminole': {
    label: 'Seminole',
    description: 'Seminole — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/bradenton': {
    label: 'Bradenton',
    description: 'Bradenton — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },
  '/areas/sarasota': {
    label: 'Sarasota',
    description: 'Sarasota — neighborhoods, FAQ, and service details.',
    category: 'city',
    relatedTags: ['locations', 'cities', 'florida'],
    parents: [{ path: '/service-areas', label: 'Service Areas' }],
  },

  // ──────────────────────────────────────────────────────────
  // Optional Pages
  // ──────────────────────────────────────────────────────────
  '/blog': {
    label: 'Blog',
    description: 'Articles on cleaning tips, home care, industry insights.',
    category: 'optional',
    relatedTags: ['blog', 'content', 'educational'],
  },
  '/policies': {
    label: 'Policies',
    description: 'Privacy policy and terms of service.',
    category: 'optional',
    relatedTags: ['legal', 'policies'],
  },
};
